import { type ReactElement, useEffect, useState } from 'react';
import { type CaseKind, type CaseLinkView } from '@vello/api-client';
import { Badge, Row } from '@vello/web-theme';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/** Where the Central console lives (the conversation). Overridable per environment via `VITE_CENTRAL_URL`. */
const DEFAULT_CENTRAL_URL = 'https://central.usevello.app';

/** A page of the Central console (the Atendimento that holds a record), opened in a new tab. */
export function centralUrl(path: string): string {
  const env = import.meta.env as { VITE_CENTRAL_URL?: string };
  return `${(env.VITE_CENTRAL_URL ?? DEFAULT_CENTRAL_URL).replace(/\/+$/, '')}${path}`;
}

/** The link to the Atendimento in the Central console. */
export function centralConversationUrl(conversationId: string): string {
  return centralUrl(`/conversation?id=${encodeURIComponent(conversationId)}`);
}

/**
 * D637 (the one case model): which Atendimento holds these records of `kind` - ONE read for a list (`GET /v1/central/case-links`, authorized by
 * the server at each conversation's own market). A record the operator's grant does not reach there, or one that is in no Atendimento, is
 * simply absent: the badge never appears, and a failed read is a silent absence (it is an addition to the screen, never its content).
 */
export function useCentralLinks(kind: CaseKind, recordIds: readonly string[]): ReadonlyMap<string, CaseLinkView> {
  const { runtime } = useAdmin();
  const [links, setLinks] = useState<ReadonlyMap<string, CaseLinkView>>(new Map());
  const key = recordIds.join(',');
  useEffect(() => {
    let live = true;
    const ids = key === '' ? [] : key.split(',');
    if (ids.length === 0) {
      setLinks(new Map());
      return undefined;
    }
    void runtime.centralCases.caseLinks(kind, ids.slice(0, 100)).then((res) => {
      if (live) setLinks(res.ok ? new Map(res.value.links.map((l) => [l.recordId, l])) : new Map());
    });
    return () => { live = false; };
  }, [runtime, kind, key]);
  return links;
}

/**
 * "Em atendimento na Central" with the link to the conversation, for a record an Operations screen shows. Renders nothing when the record
 * is in no Atendimento (or the operator's grant does not reach it). Small by design: a badge and a link.
 */
export function CentralLinkBadge({ link }: { readonly link: CaseLinkView | undefined }): ReactElement | null {
  const { t } = useT();
  const { v } = useVocab();
  if (link === undefined) return null;
  return (
    <Row gap={2} align="center" wrap>
      <Badge tone={link.status === 'open' || link.status === 'pending' ? 'warning' : 'neutral'}>{t('central.link.badge')}</Badge>
      <a className="vello-link" href={centralConversationUrl(link.conversationId)} target="_blank" rel="noreferrer">
        {t('central.link.open')} · {v('service_center.conversation_status', link.status)}
      </a>
    </Row>
  );
}

/** The badge of ONE record (a ticket, an incident, a dispute): the read and the render together. */
export function CentralLink({ kind, recordId }: { readonly kind: CaseKind; readonly recordId: string | undefined }): ReactElement | null {
  const links = useCentralLinks(kind, recordId === undefined ? [] : [recordId]);
  return <CentralLinkBadge link={recordId === undefined ? undefined : links.get(recordId)} />;
}
